// src/pages/SettingsPage.jsx
import React, { useState, useEffect } from "react";
import api from "../../../services/api";
import { useSocket } from '../../../context/SocketContext';
import { useAuth } from '../../Base/Auth/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useGeo } from '../../../context/GeoContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import "./SettingsPage.css";

function SettingsPage() {
  const { geoEnabled, setGeoEnabled } = useGeo();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const socket = useSocket();
  const [online, setOnline] = useState(socket?.connected ?? false);

  // Profile editing fields
  const [modalEmail, setModalEmail] = useState("");
  const [modalFirstName, setModalFirstName] = useState("");
  const [modalLastName, setModalLastName] = useState("");
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    if (!socket) return;
    const handleError = (msg) => {
      alert(`Ошибка сокета: ${msg}`);
    };
    socket.on("error", handleError);
    return () => {
      socket.off("error", handleError);
    };
  }, [socket]);

  // Load profile
  useEffect(() => {
    api.get("/users/profile")
      .then(resp => {
        setUser({
          username:  resp.username,
          email:     resp.email,
          firstName: resp.firstName,
          lastName:  resp.lastName,
          avatar:    resp.avatar,
          role:      resp.role,
        });
      })
      .catch(() => setError("Не удалось загрузить профиль"))
      .finally(() => setLoading(false));
  }, []);

  // Socket.IO connect/disconnect
  useEffect(() => {
    if (!socket) return;
    const onConnect    = () => setOnline(true);
    const onDisconnect = () => setOnline(false);
    socket.on("connect",    onConnect);
    socket.on("disconnect", onDisconnect);
    return () => {
      socket.off("connect",    onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [socket]);

  // Обработчик включения/выключения геолокации
// Обработчик включения/выключения геолокации
const handleGeoToggle = () => {
  if (!geoEnabled) {
    // сразу включаем UI
    setGeoEnabled(true);

    if (!("geolocation" in navigator)) {
      alert("Геолокация не поддерживается вашим браузером");
      setGeoEnabled(false);
      return;
    }

    // Запрашиваем разрешение, но НЕ ждём координат дольше 5 секунд
    navigator.geolocation.getCurrentPosition(
      () => {
        // разрешили — всё ок
      },
      err => {
        if (err.code === err.PERMISSION_DENIED) {
          alert("Нет доступа к геолокации устройства. Пожалуйста, разрешите в настройках браузера.");
          setGeoEnabled(false);
        } else {
          console.warn("Ошибка получения геолокации:", err.message);
          // можно не выключать, если хотите оставить флаг true
        }
      },
      { timeout: 5000 }
    );
  } else {
    // выключаем без задержек
    setGeoEnabled(false);
  }
};


  // Open/close modals
  const openProfileModal = () => {
    setModalEmail(user.email);
    setModalFirstName(user.firstName);
    setModalLastName(user.lastName);
    setShowProfileModal(true);
  };
  const closeProfileModal = () => setShowProfileModal(false);
  const openPasswordModal = () => setShowPasswordModal(true);
  const closePasswordModal = () => setShowPasswordModal(false);

  // Save profile changes
  const handleProfileSave = async () => {
    setLoading(true);
    setError("");
    try {
      await api.put("/users/profile", {
        email:     modalEmail,
        firstName: modalFirstName,
        lastName:  modalLastName,
      });
      setUser({ ...user, email: modalEmail, firstName: modalFirstName, lastName: modalLastName });
      setSuccessMessage("Профиль успешно обновлён");
      setTimeout(() => setSuccessMessage(""), 3000);
      closeProfileModal();
    } catch {
      setError("Ошибка при сохранении профиля");
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      alert("Новый пароль и подтверждение не совпадают");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.patch("/users/profile/password", {
        oldPassword: currentPassword,
        newPassword,
      });
      alert("Пароль успешно изменён");
      closePasswordModal();
    } catch {
      alert("Ошибка при изменении пароля");
    } finally {
      setLoading(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // Logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) return <div className="settings"><p>Загрузка...</p></div>;
  if (!user)  return <div className="settings"><p className="error-msg">Ошибка загрузки профиля</p></div>;

  return (
    <div className="settings">
      <h1>Настройки</h1>
      <div className="settings-list">
        {/* Profile */}
        <div className="profile-card">
          <img src={user.avatar} alt="Аватар" className="avatar" />
          <div className="profile-info">
            <h2>{user.firstName} {user.lastName}</h2>
            <p className="username">@{user.username} • {user.role}</p>
            <div className="status">
              <span className={`status-dot ${online ? 'online' : 'offline'}`}></span>
              <span>{online ? 'В сети' : 'Не в сети'}</span>
            </div>
          </div>
          <i className="bi bi-pencil-fill edit-icon" title="Редактировать профиль" onClick={openProfileModal} />
        </div>

        {/* Change password */}
        <div className="setting-item clickable" onClick={openPasswordModal}>
          <span><i className="bi bi-key me-2"></i>Сменить пароль</span>
        </div>

        {/* Toggles */}
        <div className="setting-item toggle-item">
          <span>Отправлять геолокацию</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={geoEnabled}
              onChange={handleGeoToggle}
            />
            <span className="slider"></span>
          </label>
        </div>
        <div className="setting-item toggle-item">
          <span>Уведомления</span>
          <label className="switch">
            <input type="checkbox" defaultChecked />
            <span className="slider"></span>
          </label>
        </div>

        <div className="setting-item clickable" onClick={() => navigate('/admin/online')}>
          <span><i className="bi bi-people-fill me-2"></i>Пользователи онлайн</span>
        </div>
        <div className="setting-item clickable" onClick={() => navigate('/logist/mob')}>
          <span><i className="bi bi-people-fill me-2"></i>Логист</span>
        </div>

        {/* Logout */}
        <div className="setting-item clickable" onClick={handleLogout}>
          <span><i className="bi bi-box-arrow-right me-2"></i>Выйти</span>
        </div>
      </div>

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Редактировать профиль</h3>
            <div className="modal-field">
              <label>Email</label>
              <input type="email" value={modalEmail} onChange={e => setModalEmail(e.target.value)} />
            </div>
            <div className="modal-field">
              <label>Имя</label>
              <input type="text" value={modalFirstName} onChange={e => setModalFirstName(e.target.value)} />
            </div>
            <div className="modal-field">
              <label>Фамилия</label>
              <input type="text" value={modalLastName} onChange={e => setModalLastName(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button onClick={handleProfileSave} disabled={loading}>Сохранить</button>
              <button onClick={closeProfileModal}>Отменить</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Сменить пароль</h3>
            <div className="modal-field">
              <label>Текущий пароль</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
            </div>
            <div className="modal-field">
              <label>Новый пароль</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            </div>
            <div className="modal-field">
              <label>Подтвердите новый</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button onClick={handleChangePassword} disabled={loading}>Изменить</button>
              <button onClick={closePasswordModal}>Отменить</button>
            </div>
          </div>
        </div>
      )}

      {error && <p className="error-msg">{error}</p>}
      {successMessage && <p className="success-msg">{successMessage}</p>}
    </div>
  );
}

export default SettingsPage;
